<template>
  <div class="radio-button">
    <input
      type="radio"
      :id="id"
      :name="name"
      :value="value"
      v-model="internalValue"
      @change="handleChange"
      :aria-checked="internalValue === value"
      :aria-labelledby="ariaLabelledby"
      :aria-describedby="ariaDescribedby"
    />
    <label :for="id">
      <span class="radio-button__custom"></span>
      <span class="radio-button__label">
        <slot></slot>
      </span>
    </label>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, ref, watch } from 'vue'

  export default defineComponent({
    name: 'RadioButton',
    props: {
      id: {
        type: String,
        required: true,
      },
      name: {
        type: String,
        required: true,
      },
      value: {
        type: [String, Object] as PropType<string | Record<string, any>>,
        required: true,
      },
      modelValue: {
        type: [String, Object, null] as PropType<string | Record<string, any> | null>,
        required: false,
        default: null,
      },
      ariaLabelledby: {
        type: String,
        required: false,
      },
      ariaDescribedby: {
        type: String,
        required: false,
      },
    },
    setup(props, { emit }) {
      const internalValue = ref(props.modelValue)

      watch(
        () => props.modelValue,
        (newValue) => {
          internalValue.value = newValue
        }
      )

      const handleChange = (event: Event) => {
        const target = event.target as HTMLInputElement
        emit('update:modelValue', props.value)
      }

      return {
        internalValue,
        handleChange,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .radio-button {
    display: flex;
    align-items: center;
    width: 100%;

    input[type='radio'] {
      opacity: 0;
      position: absolute;
      width: 0;
      height: 0;
    }

    label {
      display: flex;
      align-items: center;
      cursor: pointer;
      width: 100%;
    }

    .radio-button__custom {
      width: 28px;
      height: 28px;
      border: 1px solid $clr-primary-700;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 8px;
      position: relative;
    }

    input[type='radio']:focus + label .radio-button__custom::after {
      content: '';
      width: 12px;
      height: 12px;
      background-color: $clr-primary-300;
      border-radius: 50%;
      position: absolute;
    }

    input[type='radio']:checked + label .radio-button__custom::after {
      content: '';
      width: 12px;
      height: 12px;
      background-color: $clr-primary-700;
      border-radius: 50%;
      position: absolute;
    }

    .radio-button__label {
      font-size: 16px;
      width: 100%;
      display: flex;
      flex: 1;
    }
  }
</style>
